import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Compass, FileText, Target, Users, Scale, HandCoins, Phone, Mail, MessageCircle } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import SEO from "@/components/SEO";

const VALUES = [
  { icon: ShieldCheck, title: "Transparency", desc: "Clear scope, documented process and plain-language communication." },
  { icon: Scale, title: "Professional Integrity", desc: "No overpromises on approvals, timelines or outcomes." },
  { icon: Target, title: "Technical Precision", desc: "Modern digital surveying and careful record cross-checks." },
  { icon: Users, title: "Client Confidentiality", desc: "Your enquiry data and documents are handled with care." },
  { icon: HandCoins, title: "Accountability", desc: "We own our commitments through every step of a mandate." },
  { icon: Compass, title: "Long-Term Relationships", desc: "We'd rather earn a repeat client than close a one-off deal." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Apurba Estate"
        description="Kolkata-region real estate consultancy combining property transactions, digital land surveying, documentation assistance and property planning."
        path="/about"
      />
      <section className="relative bg-[#101827] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1626249893783-cc4a9f66880a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjBhcmNoaXRlY3R1cmFsJTIwaG91c2UlMjBpbmRpYXxlbnwwfHx8fDE3OTEwNDMzMzl8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#101827]/70" />
        </div>
        <div className="ae-container relative pt-28 pb-20">
          <div className="ae-eyebrow mb-4">About Apurba Estate</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] max-w-4xl">
              Built Around Land.<br />
              <span className="text-[#C5A46D]">Driven by Compliance.</span>
            </h1>
            <p className="mt-6 text-white/80 max-w-3xl leading-relaxed">
              Apurba Estate is a Budge Budge-based consultancy focused on agricultural land,
              green plots, digital land surveying and government record compliance across
              South 24 Parganas. Soon, we are expanding into tax, GST and finance compliance advisory.
            </p>
        </div>
      </section>

      <section className="ae-section">
        <div className="ae-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-5 text-[#1F2937] leading-relaxed">
            <p>
              Our approach combines local property knowledge, technical measurement capability,
              transparent communication and client-oriented consultancy.
            </p>
            <p>
              Whether you are exploring a plot, selling a property, planning a survey or seeking
              assistance with land records, we aim to make the process more organised and informed.
            </p>
            <p>
              We treat every mandate — small or large — with the same care. For buyers, that means
              surfacing honest comparisons. For sellers, it means clear positioning and timely coordination.
              For survey and documentation clients, it means precise outputs and realistic expectations.
            </p>
          </div>
          <aside className="lg:col-span-5">
            <div className="ae-card p-6">
              <div className="ae-eyebrow mb-3">Founder</div>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C5A46D] to-[#8E7340] flex items-center justify-center font-display text-white text-xl font-bold shrink-0">AD</div>
                <div>
                  <div className="font-bold text-[#101827] text-lg">Apu Das</div>
                  <div className="text-[#64748B] text-sm">Founder & Principal Consultant</div>
                  <div className="text-xs text-[#C5A46D] font-semibold tracking-wider uppercase mt-1">Budge Budge · South 24 Parganas</div>
                </div>
              </div>
              <p className="text-sm text-[#1F2937] mt-5 leading-relaxed">
                Apu founded Apurba Estate to make land transactions and record compliance simpler for
                landowners, farmers and first-time buyers across the Budge Budge belt. His work combines
                on-ground familiarity with the Budge Budge II BL&amp;LRO, Alipore SDLRO and South 24 Parganas
                district offices with a hands-on approach to digital land surveying.
              </p>
              <p className="text-sm text-[#64748B] mt-3 leading-relaxed">
                Clients value Apu's plain-language explanations of mutation, ROR and conversion processes —
                and his insistence on documenting the realistic timeline for each matter, not an optimistic one.
              </p>
              <div className="mt-5 pt-5 border-t border-[#E2E8F0] space-y-2">
                <a href="tel:+916289854719" className="flex items-center gap-2 text-sm text-[#101827] hover:text-[#183B66]">
                  <Phone className="w-4 h-4 text-[#C5A46D]" /> +91 62898 54719
                </a>
                <a href="mailto:apurbaestate@gmail.com" className="flex items-center gap-2 text-sm text-[#101827] hover:text-[#183B66]">
                  <Mail className="w-4 h-4 text-[#C5A46D]" /> apurbaestate@gmail.com
                </a>
                <a
                  href="https://wa.me/916289854719?text=Hello%20Apu%2C%20I%27d%20like%20to%20discuss%20a%20land%20matter."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ae-btn-whatsapp w-full justify-center mt-3"
                  data-testid="founder-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" /> Reach Apu on WhatsApp
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="ae-section bg-[#F4F6F8]">
        <div className="ae-container">
          <SectionHeader eyebrow="Our Values" title="Six things we stand for" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <div key={i} className="ae-card p-6">
                <div className="w-11 h-11 rounded-lg bg-[#101827] text-[#C5A46D] flex items-center justify-center mb-4"><v.icon className="w-5 h-5" /></div>
                <h3 className="font-semibold text-[#101827] text-lg mb-1.5">{v.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ae-section">
        <div className="ae-container">
          <div className="bg-[#101827] text-white rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="ae-eyebrow mb-2">Let's talk</div>
              <h3 className="font-display text-2xl md:text-3xl font-semibold">Have a question about our services?</h3>
            </div>
            <Link to="/contact" className="ae-btn-gold">Contact Our Team</Link>
          </div>
        </div>
      </section>
    </>
  );
}
